// Storage diagnostics (2026-10-07).
//
// Thomas's BOOX downloaded the same 55 MB book FOUR times in eight minutes
// — three complete, one aborted — and kept none of it. Every symptom he
// reported follows from that single fact: no covers (the cover is extracted
// from the stored blob), "books won't load" (each tap is a 55 MB refetch),
// and it survived both the library revert and the reload-loop fix because
// neither was the cause.
//
// The write path that loses it is autoSyncCloudLibrary's `catch {}` around
// importEpub — a bare swallow, so a QuotaExceededError on bookBlobs.put is
// indistinguishable from success from the outside. These beacons make the
// difference visible on his device in one tap.

let sent = 0;
const CAP = 25;

export function storeDiag(what: string, extra?: Record<string, string>): void {
  if (sent >= CAP) return;
  sent += 1;
  try {
    const q = new URLSearchParams({
      ev: "store",
      what,
      ...(extra ?? {}),
      ua: navigator.userAgent.slice(0, 32),
    });
    void fetch(`/__pvdiag/store?${q.toString()}`, {
      method: "GET",
      cache: "no-store",
    }).catch(() => {});
  } catch {
    /* diagnostics never break the app */
  }
}

/** Report persistence state and quota. Also ASKS for persistence — without
 *  it Android evicts large origin data under pressure, and a 55 MB epub is
 *  exactly what goes first. */
export async function reportStorageState(tag: string): Promise<void> {
  try {
    const s = navigator.storage;
    if (!s) {
      storeDiag("quota", { tag, api: "none" });
      return;
    }
    let persisted = false;
    try {
      persisted = (await s.persisted?.()) ?? false;
    } catch {
      /* ignore */
    }
    let granted = persisted;
    if (!persisted) {
      try {
        granted = (await s.persist?.()) ?? false;
      } catch {
        granted = false;
      }
    }
    let quota = 0;
    let usage = 0;
    try {
      const e = await s.estimate?.();
      quota = e?.quota ?? 0;
      usage = e?.usage ?? 0;
    } catch {
      /* ignore */
    }
    storeDiag("quota", {
      tag,
      persistedBefore: persisted ? "1" : "0",
      persistedAfter: granted ? "1" : "0",
      quotaMB: String(Math.round(quota / 1048576)),
      usageMB: String(Math.round(usage / 1048576)),
      freeMB: String(Math.round((quota - usage) / 1048576)),
    });
  } catch {
    /* never break the app for telemetry */
  }
}
